import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Save, Upload, User, Lock, Eye, EyeOff } from "lucide-react";

const input = "w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]";

export default function Profile() {
  const { user } = useAuth();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/auth/me").then((r) => setF({ name: r.data.name || "", bio: r.data.bio || "", avatar_url: r.data.avatar_url || "" })).catch(() => setF({ name: "", bio: "", avatar_url: "" }));
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("webinar_slug", "profiles");
    fd.append("kind", "images");
    try {
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("avatar_url", res.data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    }
    setUploading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", f);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [changing, setChanging] = useState(false);
  const setP = (k, v) => setPw((p) => ({ ...p, [k]: v }));

  const changePassword = async () => {
    if (pw.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (pw.new_password !== pw.confirm) return toast.error("New passwords don't match");
    setChanging(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update password");
    }
    setChanging(false);
  };


  if (!f) return <div className="p-8 text-[#9CA3AF] flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  return (
    <div className="p-8 max-w-2xl" data-testid="profile-page">
      <h1 className="text-3xl font-black tracking-tighter mb-1">Your profile</h1>
      <p className="text-[#4B5563] mb-8">This is the name, photo and bio shown when you appear as a host on an experience.</p>

      <div className="bg-white border border-[#0A0A0A] p-6 space-y-6">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-full bg-[#F1EEFB] overflow-hidden flex items-center justify-center shrink-0">
            {f.avatar_url ? <img src={f.avatar_url} alt="" className="w-full h-full object-cover" data-testid="profile-avatar" /> : <User className="w-7 h-7 text-[#9CA3AF]" />}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="profile-photo-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-4 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-50" data-testid="profile-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {f.avatar_url ? "Replace photo" : "Upload photo"}
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">Name</label>
          <input value={f.name} onChange={(e) => set("name", e.target.value)} className={input} data-testid="profile-name-input" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">Bio</label>
          <textarea value={f.bio} onChange={(e) => set("bio", e.target.value)} rows={4} placeholder="A short, warm introduction shown on your experiences." className={input} data-testid="profile-bio-input" />
        </div>
        <div className="text-xs text-[#9CA3AF]">{user?.email}</div>
        <button onClick={save} disabled={saving} className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-5 py-2.5 font-semibold flex items-center gap-2" data-testid="profile-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save profile
        </button>
      </div>

      <div className="bg-white border border-[#0A0A0A] p-6 space-y-5 mt-8" data-testid="change-password-card">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#6D28B5]" />
          <h2 className="text-lg font-bold tracking-tight">Change password</h2>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">Current password</label>
          <div className="relative">
            <input type={showPw.current ? "text" : "password"} value={pw.current_password} onChange={(e) => setP("current_password", e.target.value)} className={`${input} pr-11`} data-testid="current-password-input" autoComplete="current-password" />
            <button type="button" onClick={() => setShowPw((s) => ({ ...s, current: !s.current }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0A0A0A]" data-testid="current-password-toggle" aria-label="Toggle password visibility">
              {showPw.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">New password</label>
          <div className="relative">
            <input type={showPw.next ? "text" : "password"} value={pw.new_password} onChange={(e) => setP("new_password", e.target.value)} className={`${input} pr-11`} data-testid="new-password-input" autoComplete="new-password" />
            <button type="button" onClick={() => setShowPw((s) => ({ ...s, next: !s.next }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0A0A0A]" data-testid="new-password-toggle" aria-label="Toggle password visibility">
              {showPw.next ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">Confirm new password</label>
          <div className="relative">
            <input type={showPw.confirm ? "text" : "password"} value={pw.confirm} onChange={(e) => setP("confirm", e.target.value)} className={`${input} pr-11`} data-testid="confirm-password-input" autoComplete="new-password" />
            <button type="button" onClick={() => setShowPw((s) => ({ ...s, confirm: !s.confirm }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0A0A0A]" data-testid="confirm-password-toggle" aria-label="Toggle password visibility">
              {showPw.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <button onClick={changePassword} disabled={changing || !pw.current_password || !pw.new_password} className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-5 py-2.5 font-semibold flex items-center gap-2" data-testid="change-password-btn">
          {changing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Update password
        </button>
      </div>
    </div>
  );
}
